import { useExamCenter } from '@exam-center/exam-center-details';
import {
  useSearchExamCenterRejectedProctorRequestsQuery,
  FilterOperator,
  ExamCenterProctorRequestStep,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { type GridRowParams, type GridEventListener, NoRowsOverlay } from '@shared/components';
import { dayjs } from '@shared/utils';

import {
  BaseProctorRequestList,
  rejectedColumns,
  type RejectedProctorRequest,
} from '../../components';
import { ORDER_TYPE, PROCTOR_TYPE_VARIANT_MAP } from '../../constants';
import { useProctorRequestListSearchParams } from '../../hooks';
import { EmptyProctorRequestsOverlay } from '../empty-proctor-requests-overlay';

export default function RejectedProctorRequestList() {
  const { examCenter } = useExamCenter();
  const navigate = useNavigate();

  const [
    { page, pageSize, sortOrder, search, proctorType },
    { changePageNumber, changePageSize, changeSorting },
  ] = useProctorRequestListSearchParams();

  const { data, loading } = useSearchExamCenterRejectedProctorRequestsQuery({
    variables: {
      searchExamCenterProctorRequestsInput: {
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'verdicts.submittedAt',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['fullName'],
            value: search,
          },
          conditions: [
            {
              field: 'examCenterCode',
              value: examCenter.code,
              operator: FilterOperator.Equal,
            },
            {
              field: 'step',
              value: ExamCenterProctorRequestStep.Rejected,
              operator: FilterOperator.Equal,
            },
            {
              field: 'type',
              value: proctorType.length > 0 ? proctorType : null,
              operator: FilterOperator.In,
            },
          ].filter((condition) => condition.value !== null),
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const examCenterProctorRequests =
    data?.ecSearchExamCenterProctorRequests.examCenterProctorRequests ?? [];

  const examCenterProctorRequestItems = examCenterProctorRequests.map((proctorRequest) => ({
    id: proctorRequest.id,
    refCode: proctorRequest.refCode,
    name: [
      proctorRequest.titleTh,
      proctorRequest.firstNameTh,
      proctorRequest.middleNameTh,
      proctorRequest.lastNameTh,
    ]
      .filter(Boolean)
      .join(' '),
    type: PROCTOR_TYPE_VARIANT_MAP[proctorRequest.type],
    status: 'rejected',
    rejectedAt:
      proctorRequest.verdicts.length > 0
        ? proctorRequest.verdicts.reduce((max, curr) =>
            dayjs(curr.submittedAt).isBefore(dayjs(max.submittedAt)) ? curr : max,
          ).submittedAt
        : null,
  }));

  const totalCount = data?.ecSearchExamCenterProctorRequests.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<RejectedProctorRequest>,
  ) => {
    navigate(`/:examCenterCode/exam-proctors/requests/:requestId`, {
      params: {
        requestId: params.row.id,
        examCenterCode: examCenter.code,
      },
    });
  };

  const isSearchingOrFiltering = search.length > 0 || proctorType.length > 0;

  return (
    <BaseProctorRequestList
      columns={rejectedColumns}
      loading={loading}
      rows={examCenterProctorRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return <EmptyProctorRequestsOverlay title='ยังไม่มีคำขอผู้คุมสอบที่ไม่อนุมัติ' />;
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
